/**
 * Loads the ten M2 data files (plan §2.9): Zod-parses each, converts degrees to radians, resolves the
 * references into `GameData` (ship ids, weapon ids, AI profiles, wave numbers, ship sockets) and checks the
 * cross-file rules (the stock fit's slots and power budget, the start station). Every failure throws an Error
 * that names the file, the path and the bad id.
 *
 * Asset ids (station and prop models) are not checked here: M2-04 checks them against assets.json.
 */
import type { ZodType } from 'zod'
import type { GameData } from './index'
import {
  MODULE_IDS,
  MODULE_STATS_SCHEMAS,
  M2_FILES,
  SLOT_IDS,
  WARE_IDS,
  baseModuleId,
  careerFileSchema,
  contractsFileSchema,
  dockingFileSchema,
  encountersFileSchema,
  leaseFileSchema,
  miningFileSchema,
  moduleId,
  modulesFileSchema,
  parseModuleId,
  salvageFileSchema,
  stationsFileSchema,
  waresFileSchema,
  type CareerConfig,
  type CareerFileRaw,
  type DockingConfig,
  type DockingFileRaw,
  type EncountersConfig,
  type EncountersFileRaw,
  type M2Data,
  type M2FileKey,
  type M2RawFiles,
  type MiningConfig,
  type MiningFileRaw,
  type ModuleDef,
  type ModuleId,
  type ModuleKind,
  type ModulesData,
  type ModulesFileRaw,
  type ModuleStatsByKind,
  type SalvageConfig,
  type SalvageFileRaw,
  type SlotId,
  type StationDef,
  type StationId,
  type StationsData,
  type StationsFileRaw,
  type WaresData,
  type WaresFileRaw,
} from './m2Schemas'
import careerJson from './career.json'
import contractsJson from './contracts.json'
import dockingJson from './docking.json'
import encountersJson from './encounters.json'
import leaseJson from './lease.json'
import miningJson from './mining.json'
import modulesJson from './modules.json'
import salvageJson from './salvage.json'
import stationsJson from './stations.json'
import waresJson from './wares.json'
import { loadPatterns, type PatternsFile } from './patterns'
import { FIELD_ARCHETYPES } from './schemas'

const DEG = Math.PI / 180

/** The bundled M2 JSON files, unparsed. */
export const M2_BUNDLED: Readonly<Record<M2FileKey, unknown>> = {
  wares: waresJson,
  stations: stationsJson,
  modules: modulesJson,
  mining: miningJson,
  salvage: salvageJson,
  docking: dockingJson,
  lease: leaseJson,
  contracts: contractsJson,
  encounters: encountersJson,
  career: careerJson,
}

/** What `parseM2Data` resolves references against. */
export type M2GameRefs = Pick<GameData, 'ships' | 'weapons' | 'aiProfiles' | 'waves' | 'assets'>

/** Parses, converts and cross-validates the bundled M2 data against `game`. Call once at boot. */
export function loadM2Data(game: M2GameRefs): M2Data {
  return parseM2Data(M2_BUNDLED, game)
}

function parseFile<T>(key: M2FileKey, schema: ZodType<T>, raw: unknown): T {
  const res = schema.safeParse(raw)
  if (res.success) return res.data
  const lines = res.error.issues.map((i) => `${i.path.length > 0 ? i.path.join('.') : '(root)'}: ${i.message}`)
  throw new Error(`${M2_FILES[key]}: ${lines.join('; ')}`, { cause: res.error })
}

function refError(key: M2FileKey, path: string, message: string): Error {
  return new Error(`${M2_FILES[key]} ${path}: ${message}`)
}

/** Parses the ten files (`raw` keyed like `M2_BUNDLED`) and resolves them against `game`. Throws naming file, path and id. */
export function parseM2Data(raw: Readonly<Record<M2FileKey, unknown>>, game: M2GameRefs): M2Data {
  const files: M2RawFiles = {
    wares: parseFile('wares', waresFileSchema, raw.wares),
    stations: parseFile('stations', stationsFileSchema, raw.stations),
    modules: parseFile('modules', modulesFileSchema, raw.modules),
    mining: parseFile('mining', miningFileSchema, raw.mining),
    salvage: parseFile('salvage', salvageFileSchema, raw.salvage),
    docking: parseFile('docking', dockingFileSchema, raw.docking),
    lease: parseFile('lease', leaseFileSchema, raw.lease),
    contracts: parseFile('contracts', contractsFileSchema, raw.contracts),
    encounters: parseFile('encounters', encountersFileSchema, raw.encounters),
    career: parseFile('career', careerFileSchema, raw.career),
  }
  const modules = toModulesData(files.modules, game)
  const stations = toStationsData(files.stations)
  const career = toCareerConfig(files.career, game)
  const data: M2Data = {
    wares: toWaresData(files.wares),
    stations,
    modules,
    mining: toMiningConfig(files.mining),
    salvage: toSalvageConfig(files.salvage, game),
    docking: toDockingConfig(files.docking),
    lease: { ...files.lease, rebuild: { ...files.lease.rebuild }, insurance: { ...files.lease.insurance } },
    contracts: structuredClone(files.contracts),
    encounters: toEncountersConfig(files.encounters, game),
    career,
  }
  checkCrossFile(data, game)
  return data
}

// ---------------------------------------------------------------------------------------------
// Conversions (raw → runtime)
// ---------------------------------------------------------------------------------------------

function toWaresData(raw: WaresFileRaw): WaresData {
  return {
    wares: raw.wares.map((w, index) => ({ ...w, index, tintHex: Number.parseInt(w.tint.slice(1), 16) })),
    components: raw.components.map((c, index) => ({ ...c, massT: [c.massT[0], c.massT[1]], anchors: [c.anchors[0], c.anchors[1]], index })),
    market: structuredClone(raw.market),
  }
}

function toStationsData(raw: StationsFileRaw): StationsData {
  return {
    systemId: raw.systemId,
    stations: raw.stations.map((s, index): StationDef => {
      const { rollDeg, ...rest } = structuredClone(s)
      return { ...rest, index, roll: rollDeg * DEG }
    }),
  }
}

function toModulesData(raw: ModulesFileRaw, game: M2GameRefs): ModulesData {
  const byRaw = new Map(raw.modules.map((m) => [m.id, m]))
  const modules = MODULE_IDS.map((id): ModuleDef => {
    const m = byRaw.get(id)
    if (!m) throw refError('modules', 'modules', `missing module ${id}`)
    const { kind, mk } = parseModuleId(id)
    const stats = MODULE_STATS_SCHEMAS[kind].parse(m.stats)
    const materials = m.materials ? { materials: structuredClone(m.materials) } : {}
    return { id, kind, mk, name: m.name, price: m.price, power: m.power, stats, ...materials } as ModuleDef
  })
  const weaponIndex = (kind: 'pdc' | 'laser'): number => {
    const id = raw.weapons[kind]
    const i = game.weapons.weapons.findIndex((w) => w.id === id)
    if (i < 0) throw refError('modules', `weapons.${kind}`, `unknown weapon "${id}"`)
    return i
  }
  const slots = {} as Record<SlotId, ModuleKind[]>
  for (const slot of SLOT_IDS) slots[slot] = [...raw.slots[slot]]
  return {
    slots,
    sellFraction: raw.sellFraction,
    ...(raw.scrapFraction !== undefined ? { scrapFraction: raw.scrapFraction } : {}),
    ...(raw.materials ? { materials: structuredClone(raw.materials) } : {}),
    weapons: { ...raw.weapons },
    weaponIndex: { pdc: weaponIndex('pdc'), laser: weaponIndex('laser') },
    modules,
    byId: Object.fromEntries(modules.map((m) => [m.id, m])) as Record<ModuleId, ModuleDef>,
    ...(raw.utility ? { utility: structuredClone(raw.utility) } : {}),
  }
}

function toMiningConfig(raw: MiningFileRaw): MiningConfig {
  const { collector, ...rest } = structuredClone(raw)
  const { halfAngleDeg, ...collectorRest } = collector
  return {
    ...rest,
    collector: { ...collectorRest, halfAngle: halfAngleDeg * DEG },
    archetypeWeights: FIELD_ARCHETYPES.map((a) => WARE_IDS.map((w) => rest.archetypeOres[a][w] ?? 0)),
  }
}

type SalvageLayout = SalvageFileRaw['layouts'][string]

/** Throws unless every socket-anchored slot of `slots` names a socket on `def`'s model. */
function checkLayoutSockets(game: M2GameRefs, def: M2GameRefs['ships'][number], slots: SalvageLayout, path: string): void {
  const entry = game.assets.models.find((m) => m.id === def.assetId)
  slots.forEach((slot, i) => {
    if (!('socket' in slot.at)) return
    const socket = slot.at.socket
    if (!entry || !entry.sockets.some((s) => s.name === socket)) {
      throw refError('salvage', `${path}[${i}].at.socket`, `ship model "${def.assetId}" has no socket "${socket}"`)
    }
  })
}

/**
 * A def's salvage layout (M10 P1c2): its own, else the nearest `extends` base's (the resolved def keeps `extends`,
 * P1d), else null. An inherited layout's socket slots are checked against the derived def's model too.
 */
function layoutOf(game: M2GameRefs, layouts: Readonly<Record<string, SalvageLayout>>, def: M2GameRefs['ships'][number]): SalvageLayout | null {
  const seen = new Set<string>()
  let d: M2GameRefs['ships'][number] | undefined = def
  while (d !== undefined && !seen.has(d.id)) {
    seen.add(d.id)
    const own = layouts[d.id]
    if (own !== undefined) {
      if (d !== def) checkLayoutSockets(game, def, own, `layouts.${d.id} (inherited by ${def.id})`)
      return own
    }
    const base: string | undefined = d.extends
    d = base === undefined ? undefined : game.ships.find((s) => s.id === base)
  }
  return null
}

function toSalvageConfig(raw: SalvageFileRaw, game: M2GameRefs): SalvageConfig {
  const { beam, tractor, ...rest } = structuredClone(raw)
  const { assistDeg, ...beamRest } = beam
  const { coneDeg, ...tractorRest } = tractor
  for (const [shipId, slots] of Object.entries(rest.layouts)) {
    const def = game.ships.find((s) => s.id === shipId)
    if (!def) throw refError('salvage', `layouts.${shipId}`, `unknown ship "${shipId}"`)
    checkLayoutSockets(game, def, slots, `layouts.${shipId}`)
  }
  return {
    ...rest,
    beam: { ...beamRest, assist: assistDeg * DEG },
    tractor: { ...tractorRest, cone: coneDeg * DEG },
    layoutByDef: game.ships.map((s) => layoutOf(game, rest.layouts, s)),
  }
}

function toDockingConfig(raw: DockingFileRaw): DockingConfig {
  const { dock, ...rest } = structuredClone(raw)
  const { maxAlignDeg, ...dockRest } = dock
  return { ...rest, dock: { ...dockRest, maxAlign: maxAlignDeg * DEG } }
}

function waveIndexOf(game: M2GameRefs, wave: number, path: string): number {
  if (wave < 1 || wave > game.waves.waves.length) {
    throw refError('encounters', path, `unknown wave ${wave} (waves.json has ${game.waves.waves.length})`)
  }
  return wave - 1
}

function toEncountersConfig(raw: EncountersFileRaw, game: M2GameRefs): EncountersConfig {
  const { tiers, repo, ...rest } = structuredClone(raw)
  return {
    ...rest,
    tiers: tiers.map((t, i) => ({ ...t, waveIndex: waveIndexOf(game, t.wave, `tiers[${i}].wave`) })),
    repo: {
      ...repo,
      ships: repo.ships.map((s, i) => {
        const defIndex = game.ships.findIndex((d) => d.id === s.ship)
        if (defIndex < 0) throw refError('encounters', `repo.ships[${i}].ship`, `unknown ship "${s.ship}"`)
        const profileIndex = game.aiProfiles.profiles.findIndex((p) => p.id === s.profile)
        if (profileIndex < 0) throw refError('encounters', `repo.ships[${i}].profile`, `unknown AI profile "${s.profile}"`)
        return { ship: s.ship, profile: s.profile, defIndex, profileIndex }
      }),
    },
    contractWaveIndex: rest.contractTiers.map((w, i) => waveIndexOf(game, w, `contractTiers[${i}]`)),
  }
}

function toCareerConfig(raw: CareerFileRaw, game: M2GameRefs): CareerConfig {
  const c = structuredClone(raw)
  const shipDefIndex = game.ships.findIndex((s) => s.id === c.start.ship)
  if (shipDefIndex < 0) throw refError('career', 'start.ship', `unknown ship "${c.start.ship}"`)
  const def = game.ships[shipDefIndex]
  return { ...c, start: { ...c.start, shipDefIndex, hull: def.hull, torpedoes: def.torpedoRounds } }
}

/** Rules that span files: the start station, the stock fit (slots, power budget) and the tool sockets on the start ship. */
function checkCrossFile(data: M2Data, game: M2GameRefs): void {
  const start = data.career.start
  if (stationIndexOf(data, start.station) < 0) throw refError('career', 'start.station', `unknown station "${start.station}"`)
  for (const slot of SLOT_IDS) {
    const id = start.fitted[slot]
    const m = resolveModule(data, id)
    if (!data.modules.slots[slot].includes(m.kind)) {
      throw refError('career', `start.fitted.${slot}`, `slot ${slot} does not accept ${m.kind} modules ("${id}")`)
    }
  }
  const draw = powerDraw(data, start.fitted)
  const budget = powerBudget(data, start.fitted)
  if (draw > budget) throw refError('career', 'start.fitted', `the stock fit draws ${draw} but its power plant supplies ${budget}`)
  const entry = game.assets.models.find((m) => m.id === game.ships[start.shipDefIndex].assetId)
  const sockets = [
    ['mining.json', 'beam.socket', data.mining.beam.socket],
    ['salvage.json', 'beam.socket', data.salvage.beam.socket],
  ] as const
  for (const [file, path, socket] of sockets) {
    if (!entry || !entry.sockets.some((s) => s.name === socket)) {
      throw new Error(`${file} ${path}: the start ship "${start.ship}" has no socket "${socket}"`)
    }
  }
  const layout = data.salvage.layoutByDef[start.shipDefIndex]
  if (!layout || layout.filter((s) => s.type === 'cargoPod').length !== 1) {
    throw refError('salvage', `layouts.${start.ship}`, 'the start ship needs a layout with exactly one cargoPod (the death wreck)')
  }
}

// ---------------------------------------------------------------------------------------------
// Helpers every M2 module shares
// ---------------------------------------------------------------------------------------------

/** Index of station `id` in `data.stations.stations`, or -1. */
export function stationIndexOf(data: Pick<M2Data, 'stations'>, id: StationId): number {
  return data.stations.stations.findIndex((s) => s.id === id)
}

/** Station `id`, or throws naming it. */
export function stationDef(data: Pick<M2Data, 'stations'>, id: StationId): StationDef {
  const s = data.stations.stations.find((d) => d.id === id)
  if (!s) throw new Error(`unknown station "${id}"`)
  return s
}

let bundledPatterns: PatternsFile | null = null

/** `patterns.json`, parsed once on first use. */
function patternsData(): PatternsFile {
  bundledPatterns ??= loadPatterns()
  return bundledPatterns
}

/** Pattern-scaled values are rounded to 1e-9 so float noise (3 × 1.15 = 3.4499999999999997) never reaches a label. */
const PATTERN_ROUNDING = 1e9

/**
 * The def of module `id` (M8 MC-f2a): a base id returns exactly `byId[id]` (the same object, nothing allocated); a
 * pattern id (`pdc.mk2.varro`) returns a new def built on its base, with the pattern's strength and cost applied as
 * `value × (1 + frac)` to their stat (`power` is the power draw), and `id`, `name` and `pattern` set. Price and
 * materials are the base's (MC-f2c makes pattern modules fabricate-only). Pattern lookups allocate, so callers keep
 * them out of per-frame code (every current reader is per fit change).
 */
export function resolveModule(data: Pick<M2Data, 'modules'>, id: ModuleId, patterns: PatternsFile = patternsData()): ModuleDef {
  const base: ModuleDef | undefined = (data.modules.byId as Readonly<Partial<Record<ModuleId, ModuleDef>>>)[id]
  if (base !== undefined) return base
  const { kind, mk, pattern } = parseModuleId(id)
  const def = pattern === undefined ? undefined : patterns.patterns[pattern]
  if (pattern === undefined || def === undefined || !def.modules.includes(kind)) throw new Error(`unknown module "${id}"`)
  const from = data.modules.byId[moduleId(kind, mk)]
  const stats: Record<string, number | string> = { ...from.stats }
  let power = from.power
  for (const shift of [def.strength, def.cost]) {
    const scale = 1 + shift.frac
    if (shift.stat === 'power') {
      power = Math.round(power * scale * PATTERN_ROUNDING) / PATTERN_ROUNDING
      continue
    }
    const v = stats[shift.stat]
    if (typeof v !== 'number') throw new Error(`pattern ${pattern}: ${kind} has no numeric stat "${shift.stat}"`)
    stats[shift.stat] = Math.round(v * scale * PATTERN_ROUNDING) / PATTERN_ROUNDING
  }
  return { ...from, id, name: `${from.name} (${def.name})`, power, stats, pattern } as ModuleDef
}

/** Module `id`, base or pattern (`resolveModule`). */
export function moduleDef(data: Pick<M2Data, 'modules'>, id: ModuleId): ModuleDef {
  return resolveModule(data, id)
}

/** Stats of module `id`, which must be of `kind` (throws otherwise: a slot/kind mix-up is a bug). */
export function moduleStats<K extends ModuleKind>(data: Pick<M2Data, 'modules'>, kind: K, id: ModuleId): ModuleStatsByKind[K] {
  const m = resolveModule(data, id)
  if (m.kind !== kind) throw new Error(`module ${id} is a ${m.kind} module, not ${kind}`)
  return m.stats as ModuleStatsByKind[K]
}

/** Total power draw of a fit. */
export function powerDraw(data: Pick<M2Data, 'modules'>, fitted: Readonly<Record<SlotId, ModuleId>>): number {
  let draw = 0
  for (const slot of SLOT_IDS) draw += resolveModule(data, fitted[slot]).power
  return draw
}

/** Power budget of a fit: the fitted power plant's budget (0 when the power slot holds something else). */
export function powerBudget(data: Pick<M2Data, 'modules'>, fitted: Readonly<Record<SlotId, ModuleId>>): number {
  const m = resolveModule(data, fitted.power)
  return m.kind === 'power' ? m.stats.budget : 0
}

// ---------------------------------------------------------------------------------------------
// Inverse (runtime → raw), for the dev panel's "Copy JSON"
// ---------------------------------------------------------------------------------------------

/** Degrees back from radians, rounded to 1e-9 so a round trip returns the authored numbers. */
function toDeg(rad: number): number {
  return Math.round((rad / DEG) * 1e9) / 1e9
}

/** The file shapes of `data` (degrees back, derived fields dropped). Round-trips the bundled files exactly. */
export function m2DataToRaw(data: M2Data): M2RawFiles {
  const d = structuredClone(data)
  const m = d.mining
  const sv = d.salvage
  const dk = d.docking
  const en = d.encounters
  const st = d.career.start
  return {
    wares: {
      wares: d.wares.wares.map((w) => {
        const out: WaresFileRaw['wares'][number] = { id: w.id, name: w.name, basePrice: w.basePrice, rarity: w.rarity, tint: w.tint }
        if (w.unidentifiedName !== undefined) out.unidentifiedName = w.unidentifiedName
        return out
      }),
      components: d.wares.components.map((c) => {
        const out: WaresFileRaw['components'][number] = {
          type: c.type,
          name: c.name,
          baseValue: c.baseValue,
          massT: c.massT,
          anchors: c.anchors,
          propAsset: c.propAsset,
        }
        if (c.hazard !== undefined) out.hazard = c.hazard
        return out
      }),
      market: d.wares.market,
    },
    stations: {
      systemId: d.stations.systemId,
      stations: d.stations.stations.map((s) => ({
        id: s.id,
        name: s.name,
        faction: s.faction,
        look: s.look,
        assetId: s.assetId,
        baySocket: s.baySocket,
        position: s.position,
        faceToward: s.faceToward,
        rollDeg: toDeg(s.roll),
        services: s.services,
        outfitting: s.outfitting,
        prices: s.prices,
        sells: s.sells,
        capacityT: s.capacityT,
        commsSeed: s.commsSeed,
        ...(s.desk !== undefined ? { desk: s.desk } : {}),
      })),
    },
    modules: {
      slots: d.modules.slots,
      sellFraction: d.modules.sellFraction,
      ...(d.modules.scrapFraction !== undefined ? { scrapFraction: d.modules.scrapFraction } : {}),
      ...(d.modules.materials ? { materials: structuredClone(d.modules.materials) } : {}),
      weapons: d.modules.weapons,
      modules: d.modules.modules.map((x) => ({
        id: baseModuleId(x.id),
        name: x.name,
        price: x.price,
        power: x.power,
        stats: { ...x.stats },
        ...(x.materials ? { materials: structuredClone(x.materials) } : {}),
      })),
      ...(d.modules.utility ? { utility: structuredClone(d.modules.utility) } : {}),
    },
    mining: {
      massPerCubicMetre: m.massPerCubicMetre,
      fractureMassT: m.fractureMassT,
      fractureValue: m.fractureValue,
      archetypeOres: m.archetypeOres,
      mix: m.mix,
      tetherstone: m.tetherstone,
      beam: m.beam,
      booms: m.booms,
      scan: m.scan,
      chip: m.chip,
      collector: {
        range: m.collector.range,
        halfAngleDeg: toDeg(m.collector.halfAngle),
        pullAccel: m.collector.pullAccel,
        maxPullSpeed: m.collector.maxPullSpeed,
        captureDistance: m.collector.captureDistance,
      },
      fracture: m.fracture,
      heat: m.heat,
    },
    salvage: {
      beam: {
        range: sv.beam.range,
        socket: sv.beam.socket,
        assistDeg: toDeg(sv.beam.assist),
        anchorRadius: sv.beam.anchorRadius,
        spillRadius: sv.beam.spillRadius,
        cutSeconds: sv.beam.cutSeconds,
      },
      tractor: {
        coneDeg: toDeg(sv.tractor.cone),
        pullAccel: sv.tractor.pullAccel,
        maxSpeed: sv.tractor.maxSpeed,
        captureDistance: sv.tractor.captureDistance,
        holdPoint: sv.tractor.holdPoint,
      },
      coolant: sv.coolant,
      bands: sv.bands,
      tiers: sv.tiers,
      rush: sv.rush,
      graveyardSites: sv.graveyardSites,
      recondition: sv.recondition,
      coreDelivery: sv.coreDelivery,
      hulks: sv.hulks,
      wrecks: sv.wrecks,
      layouts: sv.layouts,
      deathWreck: sv.deathWreck,
      derelict: sv.derelict,
    },
    docking: {
      // The file's optional note (SC-5a1a), first as in docking.json so Copy JSON keeps the file's key order.
      ...(dk.comment === undefined ? {} : { comment: dk.comment }),
      requestRange: dk.requestRange,
      clearanceSeconds: dk.clearanceSeconds,
      autodockRange: dk.autodockRange,
      corridor: dk.corridor,
      speedLimits: dk.speedLimits,
      dock: { maxSpeed: dk.dock.maxSpeed, maxAlignDeg: toDeg(dk.dock.maxAlign), holdSeconds: dk.dock.holdSeconds },
      impact: dk.impact,
      launch: dk.launch,
      autopilot: dk.autopilot,
    },
    lease: d.lease,
    contracts: d.contracts,
    encounters: {
      ...(en.comment === undefined ? {} : { comment: en.comment }),
      intervalSeconds: en.intervalSeconds,
      baseChance: en.baseChance,
      cargoValueScale: en.cargoValueScale,
      maxChance: en.maxChance,
      tiers: en.tiers.map((t) => ({ minCargoValue: t.minCargoValue, wave: t.wave })),
      spawnDistance: en.spawnDistance,
      stationExclusion: en.stationExclusion,
      despawnDistance: en.despawnDistance,
      maxGroups: en.maxGroups,
      repo: {
        ships: en.repo.ships.map((x) => ({ ship: x.ship, profile: x.profile })),
        undockDelaySeconds: en.repo.undockDelaySeconds,
        inSpaceWithinSeconds: en.repo.inSpaceWithinSeconds,
      },
      contractTiers: en.contractTiers,
      ...(en.bandScale === undefined ? {} : { bandScale: en.bandScale }),
    },
    career: {
      start: { wallet: st.wallet, station: st.station, ship: st.ship, fitted: st.fitted, standings: st.standings },
      repairPerHull: d.career.repairPerHull,
      torpedoPrice: d.career.torpedoPrice,
      goods: d.career.goods,
      berth: d.career.berth,
      ...(d.career.rushSpares !== undefined ? { rushSpares: d.career.rushSpares } : {}),
      snapshotHz: d.career.snapshotHz,
      deathRespawnSeconds: d.career.deathRespawnSeconds,
      save: d.career.save,
      ...(d.career.ship !== undefined ? { ship: d.career.ship } : {}),
    },
  }
}
